import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from "react";
import {
    useGetDUCUrlQuery,
    useSubmitDUCMutation,
    useReSubmitDUCMutation,
    useAddSubApplicantMutation
} from "../../api/ducApiSlice";
import { Controller, useForm } from "react-hook-form";
import { LassoTextField } from "../../components/LassoTextField";
import { colours } from "../../StyleConstants";
import StepperDUC from "./StepperDUC";
import LassoModal from "../../components/LassoModal";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface FormValues {
    businessName: string
    businessEmail: string
}

const AuthorizedOfficial = (props: any) => {
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<FormValues>({
        //the reason we needed local storage here is because if the user submits their request and then for some
        //reason leaves the page or refreshes when they continue the flow they will be brought back to this component
        //we needed a way to prepopulate the fields with the values they have already inputted.
        //The fromStatement is because the information in the local storage will persist and we only want
        //to prepopulate if the user has already completed a submission and is brought directly to this page
        //if they are working their way through the flow this should not get populated (unless they are performing a resubmission)

        defaultValues: {
            businessName:  props.prevData?.data?.businessName ? props.prevData?.data?.businessName : "",
            businessEmail: props.prevData?.data?.businessEmail? props.prevData?.data?.businessEmail : ""
        },
    });

    const [id, setID] = useState(0)
    // Initialize loading as true if we're in a submitted state to prevent flickering
    const [loading, setLoading] = useState(props?.data?.status === "Submitted")
    const [loadingEdit, setLoadingEdit] = useState(false)
    const [continueButton, setContinueButton] = useState(false)
    const [disabled, setDisabled] = useState(props?.data?.status === "Submitted");
    const [disabledEdit, setDisabledEdit] = useState(false)
    const [skip, setSkip] = useState(props?.data?.status !== "Submitted")
    const { data: urlCall, isFetching: urlCallLoading } = useGetDUCUrlQuery(
        { id: props?.data?.ducDetailsId || id },
        { skip: skip }
    )
    const [submitDUC] = useSubmitDUCMutation();
    const [reSubmitDUC] = useReSubmitDUCMutation();
    const [addSub] = useAddSubApplicantMutation();
    const [url, setUrl] = useState("")
    const [reSubmitted, setResubmitted ] = useState(false)
    const [addedSub, setAddedSub] = useState(false)
    const [openEditSign, setOpenEditSign] = useState(false)
    const [firstRender, setFirstRender] = useState(false)
    const submittedRef = useRef(props?.data?.status === "Submitted");
    // One-shot guards to prevent repeated submissions/resubmissions on re-renders
    const resubmitFiredRef = useRef(false)
    const addSubFiredRef = useRef(false)
    const [resubmitting, setResubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if(!props.prevData?.data){
            setFirstRender(true)
        }
        else if(props.prevData?.data && firstRender){
            setValue("businessName", props.prevData?.data?.businessName)
            setValue("businessEmail", props.prevData?.data?.businessEmail)
        }
    }, [props.prevData]);

    //Once the status is submitted we know the submission is complete
    //we disable the back button and set the id which is needed to get the singing url
    useEffect(() => {
        // Only update state if status changes to "Submitted" after initial render
        if(props?.data?.status === "Submitted"){
            setDisabled(true)
            setLoading(true) // Set loading state before triggering the API call
            setID(props.data?.ducDetailsId)
            setSkip(false) // Trigger API call after setting loading state
        }

    }, [props.data])

    //File is a bad name the call useGetDUCUrlQuery used to return a file
    //it does not anymore it returns a string I just forgot to update this name
    //this useffect is basically watching the useGetDUCUrlQuery call and
    //setting our url once we get it from the BE
    useEffect(() => {
        // Only process the urlCall if it contains data and meets one of the conditions
        if(urlCall && (addedSub || reSubmitted || submittedRef.current)){
            // Set the URL first before changing any UI states
            setUrl(urlCall.url)

            // Then update UI states in a single batch to minimize re-renders
            setTimeout(() => {
                setLoading(false)
                setContinueButton(true)
                if(reSubmitted){
                    setLoadingEdit(false)
                    setOpenEditSign(true)
                }
                // Submission cycle is complete for any path; allow DUCProcess to resume normal navigation guards
                if (typeof props.setSubmitting === 'function') {
                    props.setSubmitting(false)
                }
            }, 0)
        }

    }, [urlCall])

    //this useEffect is used to submit the duc application
    //the reason it's in a useEffect is because the submit was happening
    //before the buissnessName and buisnessOfficial were being set in DUCInfo
    //hence we wait for them to be set before we submit
    //The reSubmitted is there because there was some issue after
    //a resubmit where this useEffect was getting called over and over
    //causing resubmit calls to continuously happen
    useEffect(() => {
        if (props.DUCInfo["businessName"]) {
            // Avoid re-entry after we have completed any of these actions
            if (reSubmitted || addedSub) {
                return;
            }

            if (props.isEdit && !props.isAddSub) {
                // Guard: ensure resubmit is fired only once per user action
                if (resubmitFiredRef.current) return;
                resubmitFiredRef.current = true;
                setResubmitting(true);

                // Build payload: for group renewals, ensure sub-applicants are included even if the user didn't revisit the step
                const payloadData: any = { ...props.DUCInfo };
                if (props.isGroup) {
                    const infoSubs = Array.isArray((props.DUCInfo as any)?.subApplicants) ? (props.DUCInfo as any).subApplicants : undefined;
                    // Normalize legacy alias from prevData if present
                    const prev = (props.prevData as any)?.data || {};
                    const prevSubs = Array.isArray(prev?.subApplicants)
                        ? prev.subApplicants
                        : (Array.isArray(prev?.subRecipients) ? prev.subRecipients : undefined);
                    const mergedSubs = infoSubs && infoSubs.length > 0 ? infoSubs : (prevSubs && prevSubs.length > 0 ? prevSubs : undefined);
                    if (mergedSubs && mergedSubs.length > 0) {
                        payloadData.subApplicants = mergedSubs;
                    }
                }

                reSubmitDUC({ data: { data: payloadData }, id: props?.data?.ducDetailsId })
                    .then(() => {
                        props.refetch();
                        setResubmitted(true);
                        setError(null)
                    })
                    .catch((e) => {
                        // On failure, allow retry and surface error
                        resubmitFiredRef.current = false;
                        setError(e?.data?.message || e?.message || 'Failed to resubmit. Please try again.');
                    })
                    .finally(() => {
                        setResubmitting(false);
                    });
            } else if (props.isAddSub) {
                // Guard: ensure add-sub is fired only once per user action
                if (addSubFiredRef.current) return;

                // Gather sub-applicants from the most reliable sources, preferring DUCInfo
                let subs: any[] | undefined = Array.isArray((props.DUCInfo as any)?.subApplicants)
                    ? (props.DUCInfo as any).subApplicants
                    : undefined;
                if (!subs || subs.length === 0) {
                    // Fallback to previous data (supports legacy key 'subRecipients')
                    const prev = (props.prevData as any)?.data || {};
                    const prevSubs = Array.isArray(prev?.subApplicants)
                        ? prev.subApplicants
                        : (Array.isArray(prev?.subRecipients) ? prev.subRecipients : []);
                    subs = prevSubs;
                }

                if (!subs || subs.length === 0) {
                    // Preconditions not met; avoid spinner deadlock and inform the user
                    setLoading(false);
                    if (typeof props.setSubmitting === 'function') {
                        props.setSubmitting(false)
                    }
                    setError('Please add at least one Sub-Applicant before continuing.');
                    return;
                }

                // Determine which sub-applicants are newly added compared to the previous state
                const prev = (props.prevData as any)?.data || {};
                const prevEmails = new Set(
                    (Array.isArray(prev?.subApplicants) ? prev.subApplicants : (Array.isArray(prev?.subRecipients) ? prev.subRecipients : []))
                        .map((s: any) => (s?.email ?? '').trim().toLowerCase())
                );
                const annotatedSubs = (subs || []).map((s: any) => {
                    const email = (s?.email ?? '').trim().toLowerCase();
                    const isNew = !prevEmails.has(email);
                    // Preserve existing newlyAddedUser if already set; otherwise compute
                    const flag = typeof s?.newlyAddedUser === 'boolean' ? s.newlyAddedUser : isNew;
                    return { ...s, newlyAddedUser: flag };
                });

                addSubFiredRef.current = true;
                setLoading(true);
                addSub({ data: { ...props.DUCInfo, ducDetailsId: props?.data?.ducDetailsId, subApplicants: annotatedSubs } })
                    .then(() => {
                        props.refetch();
                        setAddedSub(true);
                    })
                    .catch((e: any) => {
                        // Allow retry on failure and stop spinner
                        addSubFiredRef.current = false;
                        setLoading(false);
                        if (typeof props.setSubmitting === 'function') {
                            props.setSubmitting(false)
                        }
                        setError(e?.data?.message || e?.message || 'Failed to add sub-applicants. Please try again.');
                    });
            } else if (!submittedRef.current) {
                setLoading(true);
                // LC-6431: submitDUC's trigger promise always resolves (RTK Query only rejects via
                // .unwrap()), so a plain .then() ran on both success AND failure - e.g. the original
                // PI cancelling an in-progress ownership transfer now returns a 409 with a validation
                // message instead of a 500, but this still marked the DUC submitted and never reset
                // the spinner, leaving "Sign my DUC" stuck loading forever with no feedback.
                submitDUC({ data: props.DUCInfo }).unwrap()
                    .then(() => {
                        submittedRef.current = true;
                        props.refetch();
                    })
                    .catch((e: any) => {
                        setLoading(false);
                        if (typeof props.setSubmitting === 'function') {
                            props.setSubmitting(false)
                        }
                        setError(typeof e?.data === 'string'
                            ? e.data
                            : (e?.data?.message || e?.message || 'Failed to submit your DUC. Please try again.'));
                    });
            }
        }
    }, [props.DUCInfo]);

    const handleClick = (newData: FormValues) => {
        props.setSubmitting(true)
        setLoading(true)
        setLoadingEdit(true)
        props.setDUCInfo({ ...props.DUCInfo, ...newData });
        setDisabledEdit(true)
    }

    const handleClickBack = () => {
        props.setCurStep(props.curStep -1)
    }

    const handleContinue = () => {
        window.open(url,"_self")
    }

    const handleClickResubmit = () => {
        window.open(url,"_self")
    }

    const isRenewal = props.isRenewal || props.expiry || props.DUCInfo?.type === 'Renewal';
    return (
        <Stack flexDirection="column" height="100%" gap={4}>
            <Stack flexDirection="column" gap={4}>

                <StepperDUC
                    activeStep={props.curStep}
                    stepLabels={props.stepLabels} />

            </Stack>

            <Box>
                <Typography variant="h2" fontSize="20px"
                    fontWeight="600">
                        Authorized Institutional Business Official
                </Typography>

                <Typography
                    fontWeight="400"
                >
                    List an individual with an Authorized Institutional Business Official role as defined in the <a
                        href="https://www.era.nih.gov/register-accounts/account-roles.htm" target="_blank" rel="noopener noreferrer"> NIH eRA Commons</a>.
                </Typography>
            </Box>
            <Alert
                icon={<InfoOutlinedIcon
                    fontSize="inherit"
                    sx={{ color: colours.notifications.info }} />}
                sx={{
                    backgroundColor: colours.notifications.info10,
                    alignSelf: "flex-start"
                }
                }

            >
                <Typography fontSize={"14px"}>
                    <strong>NIH Users Only: Please enter the short version of your Signing Official’s email address. For more details, <a target={"_blank"}
                        rel={'noopener noreferrer'}
                        href="https://www.nbdc-datahub.org/faqs"
                    >
                            refer to FAQs
                    </a>
                    </strong>
                </Typography>
            </Alert>
            {error && (
                <Alert severity="error">
                    {error}
                </Alert>
            )}
            <Stack flexDirection="column" gap={4} width="100%">
                <Controller
                    name={"businessName"}
                    control={control}
                    rules={{ required: "This field is required", pattern: /^(?!\s*$).+/ }}
                    render={({ field }) =>
                        <LassoTextField
                            id="duc-authorized-official-name"
                            label="Name"
                            fullWidth
                            required
                            disabled={((disabled || loading) && !props.isEdit) || ((disabledEdit || loading) && props.isEdit)}
                            error={'businessName' in errors}
                            helperText={errors?.businessName?.message || ""}
                            {...field}
                        />}
                />
                <Controller
                    name={"businessEmail"}
                    control={control}
                    rules={{
                        required: "This field is required", pattern: {
                            value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                            message: 'Invalid email address',
                        }
                    }}
                    render={({ field }) =>
                        <LassoTextField
                            id="duc-authorized-official-email"
                            label="Email"
                            fullWidth
                            disabled={((disabled || loading) && !props.isEdit) || ((disabledEdit || loading) && props.isEdit)}
                            required
                            error={'businessEmail' in errors}
                            helperText={errors?.businessEmail?.message || ""}
                            {...field}
                        />}
                />
            </Stack>
            <Stack
                flexDirection="row"
                justifyContent="space-between">

                {!props.isEdit &&
                    <Button
                        id={"duc-flow-back-button"}
                        variant="tertiary"
                        onClick={handleClickBack}
                        sx={{ alignSelf: "center" }}
                        disabled={(disabled || loading)}
                    >
                        Back
                    </Button>
                }

                {props.isEdit &&
                    <Button
                        id={"duc-flow-back-button"}
                        variant="tertiary"
                        onClick={handleClickBack}
                        sx={{ alignSelf: "center" }}
                        disabled={disabledEdit || loading || props.isSub}
                    >
                        Back
                    </Button>
                }

                {!props.isEdit &&
                    <Button
                        id={"duc-submit-button"}
                        variant="primary"
                        onClick={handleSubmit(handleClick)}
                        sx={{ alignSelf: "center" }}
                        disabled={disabled || loading}
                    >
                    Submit
                    </Button>}

                {props.isEdit && !props.isAddSub &&
                    <Button
                        id={"duc-resubmit-button"}
                        variant="primary"
                        onClick={handleSubmit(handleClick)}
                        sx={{ alignSelf: "center" }}
                        disabled={disabledEdit || loading || resubmitting || props.isSub}
                    >
                        {resubmitting ? 'Resubmitting…' : 'Resubmit'}
                    </Button>}

                {props.isAddSub &&
                    <Button
                        id={"duc-authorized-official-add-subapplicants-button"}
                        variant="primary"
                        onClick={handleSubmit(handleClick)}
                        sx={{ alignSelf: "center" }}
                        disabled={loading || disabled || props.isSub}
                    >
                        Add sub-applicants
                    </Button>}


            </Stack>

            {(loading || urlCallLoading) && (!props.isEdit || props.isAddSub) &&
            <Stack width="100%" flexDirection="column" alignItems="center">
                <CircularProgress sx={{ width: "250px", height: "250px" }} />
            </Stack>}

            {loadingEdit && props.isEdit && !props.isAddSub &&
                <Stack width="100%" flexDirection="column" alignItems="center">
                    <CircularProgress sx={{ width: "250px", height: "250px" }} />
                </Stack>}

            {continueButton && !props.isSub && !props.isEdit &&
                <Button
                    id={"duc-authorized-official-add-new-subapplicants-sign-duc-button"}
                    variant="primary"
                    onClick={handleContinue}
                    sx={{ alignSelf: "center" }}
                >
                    {isRenewal ? "Continue to Signing" : (props.isSecond ? "Add new Sub-Applicants (Sign new DUC)" : "Sign my DUC")}
                </Button>
            }

            {continueButton && props.isAddSub &&
                <Button
                    variant="primary"
                    onClick={handleContinue}
                    sx={{ alignSelf: "center" }}
                >
                    {isRenewal ? "Continue to Signing" : "Add new Sub-Applicants (Sign new DUC)"}
                </Button>
            }

            {props.isSub &&
                <Typography textAlign='center'>
                    Your PI has submitted the form but has not completed signing once they have signed
                    you will be able to view the full form
                </Typography>

            }

            <LassoModal open={openEditSign}>
                <Stack flexDirection="column" gap={4}>
                    <Typography textAlign="center">
                        DUC has been successfully resubmitted
                    </Typography>
                    <Button
                        id={"duc-authorized-official-sign-duc-button"}
                        variant="primary"
                        onClick={handleClickResubmit}
                        disabled={!url}
                    >
                        {url ? 'Sign my DUC' : 'Preparing signing link…'}
                    </Button>
                </Stack>
            </LassoModal>
        </Stack>
    )
}

export default AuthorizedOfficial;
